import { CircularProgress, Grid } from '@mui/material';
import { gridStateActions } from 'app/components/Common/Grid/GridStateManagement/GridState.redux';
import { DetailsPage } from 'app/layout/pages/DetailsPage';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { PassportCredentialXGrid } from './PassportCredentialXGrid';
import { PassportCredSearchChips } from './Filters/PassportCredSearchChips';
import { PassportCredSearchFilters } from './Filters/PassportCredSearchFilters';
import { getFilteredData, passportCredSearchFilterName } from './Filters/PassportCredFilterUtility';
import { defaultPassportCredFilters } from './Filters/IPassportCredFilter';
import { selectPlacementStatus } from '../../../../../../store/redux-store/placement-status/selectors';
import { placementStatus } from './Helpers/PassportCredentialHelper';
import { revalidateChipSelection } from 'app/components/GlobalSearch/helper';
import { selectGridState } from 'app/components/Common/Grid/GridStateManagement/GridState.selectors';
import {
  selectMiscellaneousPlacementData,
  selectPassportCredentials,
  selectPlacementHeaderData,
} from '../../../../../../store/redux-store/placement-details/selectors';
import { useParams } from 'react-router-dom';
import _isEmpty from 'lodash/isEmpty';
import _ from 'lodash';
import { placementDetailsAction } from '../../../../../../store/redux-store/placement-details/slice';
import { MarkExceptionDialog } from '../CredentialingTab/Content/Requirements/MarkException/MarkExceptionDialog';
import { ExceptionDialogStateSelection } from '../CredentialingTab/Store/PlacementEventDetails.selector';
import { placementEventActions } from '../CredentialingTab/Store/PlacementEventDetails.redux';
import { usePromiseTracker } from 'react-promise-tracker';

interface RouteParams {
  placementId: string;
}

export const PassportCredentialTab = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const gridTag = 'PassportCredentialGrid';
  const { currentPlacementStatus } = useSelector(selectPlacementStatus);
  const [isEnabled, setIsEnabled] = useState(false);
  const gridState = useSelector(selectGridState);
  const placementDetails = useSelector(selectPlacementHeaderData);
  const passportCredentials = useSelector(selectPassportCredentials);
  const { passportChecklist } = useSelector(selectMiscellaneousPlacementData);
  const exceptionDialogState = useSelector(ExceptionDialogStateSelection);
  const [credentials, setCredentials] = useState<any[]>([]);
  const [filterData, setFilterData] = useState<any[]>([]);
  const [progress, setProgress] = useState(false);
  const params = useParams<RouteParams>();
  const { promiseInProgress: onChecklistDataFetch } = usePromiseTracker({
    area: 'passport-checklist-items',
    delay: 0,
  });

  const handleReset = () => {
    if (isEnabled) {
      fetchData();
    } else {
      setCredentials([]);
      setFilterData([]);
    }
  };

  useEffect(() => {
    setProgress(onChecklistDataFetch);
  }, [onChecklistDataFetch]);

  useEffect(() => {
    if (passportCredentials) {
      const sortedData = dataFormatter(passportCredentials);
      setCredentials(sortedData);
      setFilterData(sortedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passportCredentials]);

  const fetchData = async () => {
    if (placementDetails && placementDetails.candidate.id !== 0) {
      if (passportCredentials && passportCredentials.length > 0) {
        const sortedData = dataFormatter(passportCredentials);
        setCredentials(sortedData);
        setFilterData(sortedData);
        setProgress(false);
      } else {
        dispatch(placementDetailsAction.getPassportChecklist({ placementId: parseInt(params.placementId) }));
      }
    }
  };

  useEffect(() => {
    if (passportChecklist?.length! > 0 && isEnabled) {
      const sortedData = dataFormatter(passportChecklist);
      dispatch(placementDetailsAction.setPassportCredentials(sortedData));
      setCredentials(sortedData);
      setFilterData(sortedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passportChecklist]);

  const sortData = (licenses, index) => {
    const sortListed = _.orderBy(
      licenses,
      (o: any) => {
        return o != null ? o[index] : o;
      },
      ['asc'],
    );

    return sortListed;
  };

  const dataFormatter = data => {
    const cloneData = _.cloneDeep(data);
    let sortedData: any[] = [];
    let compliantData: any[] = [];
    let nonCompliantData: any[] = [];
    compliantData = cloneData.filter(d => d.isCompliant === true) || [];
    nonCompliantData = cloneData.filter(d => d.isCompliant === false) || [];

    sortedData = [...sortData(nonCompliantData, 'category'), ...sortData(compliantData, 'category')];

    sortedData = sortedData?.map((item, index) => {
      item.exceptionWaiveType = item.exceptionWaiveType ? item.exceptionWaiveType : '--';
      item.status = item.isCompliant
        ? t('placement.profile.tabs.passportCredentialTab.isCompliantTrue')
        : t('placement.profile.tabs.passportCredentialTab.isCompliantFalse');
      item.id = index;
      return item;
    });
    return sortedData;
  };

  useEffect(() => {
    if (isEnabled) {
      setProgress(true);
      fetchData();
    } else {
      setCredentials([]);
      setFilterData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled]);

  const defaultValues = {
    [passportCredSearchFilterName.category]: [],
    [passportCredSearchFilterName.status]: [],
  };
  const formMethods = useForm({ defaultValues });
  const { handleSubmit } = formMethods;

  const onApplyFilter = (values, newSelectedChipFilters?) => {
    setProgress(true);
    dispatch(gridStateActions.setPageNumber(1));
    const selectedChipFilters =
      newSelectedChipFilters || revalidateChipSelection(values, gridState.filterAttributes?.selectedChipFilters || []);
    dispatch(
      gridStateActions.setFilterAttributes({
        ...gridState.filterAttributes,
        filterObject: Object.entries(values),
        selectedChipFilters,
      }),
    );

    if (values && !_isEmpty(values)) {
      const filteredData = getFilteredData(values, _.cloneDeep(credentials), newSelectedChipFilters);

      if (filteredData.length > 0) {
        setFilterData(filteredData);
      } else {
        setFilterData(_.cloneDeep(credentials));
      }
    }
    setProgress(false);
  };

  useEffect(() => {
    dispatch(
      gridStateActions.setFilterAttributes({
        filterObject: Object.entries(defaultValues),
        selectedChipFilters: {
          ...defaultPassportCredFilters,
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentPlacementStatus) {
      if (placementStatus.find(d => d.name.toLowerCase() === currentPlacementStatus.activityStatus.toLowerCase())) {
        setIsEnabled(true);
      } else {
        setIsEnabled(false);
      }
    }
  }, [currentPlacementStatus]);

  const Loading = () => (
    <div style={{ display: 'flex', alignContent: 'center', justifyContent: 'center' }}>
      <CircularProgress />
    </div>
  );

  const handleCloseDialog = () => {
    dispatch(
      placementEventActions.setExceptionDialogState({
        open: false,
        add: false,
        edit: false,
        credential: false,
      }),
    );
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(filter => onApplyFilter(filter))}>
          <DetailsPage
            enablePageActionSpace
            filters={
              <PassportCredSearchFilters
                defaultValues={defaultValues}
                isEnabled={isEnabled}
                handleReset={handleReset}
              />
            }
            chips={<PassportCredSearchChips onApplyFilter={onApplyFilter} />}
            stickIconArray={[4]}
            elevation={0}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {credentials && credentials?.length === 0 && progress ? (
                  <Loading />
                ) : (
                  <PassportCredentialXGrid
                    loading={progress}
                    gridTag={gridTag}
                    isEnabled={isEnabled}
                    credentials={filterData}
                  />
                )}
              </Grid>
            </Grid>
          </DetailsPage>
        </form>
      </FormProvider>
      {exceptionDialogState?.open && (
        <MarkExceptionDialog
          open={exceptionDialogState?.open || false}
          edit={exceptionDialogState?.edit || false}
          add={exceptionDialogState?.add || false}
          credential={exceptionDialogState?.credential || null}
          handleClose={handleCloseDialog}
        />
      )}
    </>
  );
};
